import React, {Fragment, useEffect, useState} from 'react';
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import {makeStyles} from "@material-ui/core/styles";
import {useApiNotification} from "../../../../ui/ApiNotificationProvider";
import {useTranslation} from "react-i18next";
import {ProvidersBrowserSimple} from "../../../shared/providers/ProvidersBrowserSimple";
import Checkbox from '@material-ui/core/Checkbox';
import CheckIcon from '@material-ui/icons/Check';
import WarningIcon from '@material-ui/icons/Warning';
import CircularProgress from '@material-ui/core/CircularProgress';
import {addGroupProvider, removeProGroupProvider} from "../../../../../api/pro-guest-functions";
import * as _ from 'lodash';
import {ConfirmationPrompt} from "../../../../ui/ConfirmationPrompt";
import {GreyedOutProgressMask} from "../../../../ui/GreyedOutProgressMask";
import {run} from "../../../../../utils/function-util";

const useStyles = makeStyles(theme => ({
    root: {
        '& .MuiTextField-root': {
            margin: theme.spacing(2),
            width: 300,
        },
    },
    actions: {
        '& > *': {
            marginRight: theme.spacing(2),
        },
    },
    actionRow: {
        display: 'flex',
        justifyContent: 'space-between'
    }
}));
const ProcessingStatus = {
    none: 'none',
    processing: 'processing',
    successful: 'successful',
    failed: 'failed'
};
const GroupProviders = ({organizationId, folderId, groupId, onClose}: any) => {
    const classes = useStyles();
    const {handleError} = useApiNotification();
    const [submitted, setSubmitted] = useState(false);
    const [processingState, setProcessingState] = useState<any>({});
    const [selectedProviders, setSelectedProviders] = useState<any>({});
    const processing = _.values(processingState).some((s: any) => s === ProcessingStatus.processing);
    const [refresh, setRefresh] = useState(0);
    const [userToDelete, setUserToDelete] = useState<any>();
    const [disableDelete, setDisableDelete] = useState(false);
    const [addMode, setAddMode] = useState(false);
    const {t} = useTranslation();

    const onSave = async () => {
        setSubmitted(true);
        const providersToAdd = _.keys(_.pickBy(selectedProviders, (selected: any) => selected));
        for (let providerId of providersToAdd) {
            setProcessingState((state: any) => ({...state, [providerId]: ProcessingStatus.processing}));
            await addGroupProvider({organizationId, folderId, groupId, providerId})
                .then(() => {
                    setProcessingState((state: any) => ({...state, [providerId]: ProcessingStatus.successful}));
                    setSelectedProviders((state: any) => ({...state, [providerId]: false}));
                })
                .catch(err => {
                    setProcessingState((state: any) => ({...state, [providerId]: ProcessingStatus.failed}));
                });
        }
    };
    const onProviderChecked = (provider: any, e: any) => {
        if (submitted)
            setSubmitted(false);
        const checked = e.target.checked;
        setSelectedProviders((state: any) => ({...state, [provider.id]: checked}));
    };
    const selectProAction = (provider: any) => {
        let action = <Checkbox disabled={processing}
                               checked={selectedProviders[provider.id] || false}
                               onChange={e => onProviderChecked(provider, e)}
                               color="primary"
                               inputProps={{'aria-label': 'primary checkbox'}}/>;
        if (processingState[provider.id] === ProcessingStatus.processing)
            action = <CircularProgress size={20} color="inherit"/>;
        else if (processingState[provider.id] === ProcessingStatus.successful)
            action = <CheckIcon className={(classes as any).successLabel}/>;
        else if (processingState[provider.id] === ProcessingStatus.failed)
            action = <WarningIcon color='error'/>;
        return action;
    };
    const removeProAction = (user: any) => <Button color="primary" size="small" onClick={() => removeProvider(user)}>
        {t('ProGuest.Providers.RemovePro.Title')}
    </Button>;
    const removeProvider = (user: any) => {
        setUserToDelete(user);
    };
    const deleteProvider = async () => {
        setDisableDelete(true);
    };
    useEffect(run(async () => {
        if (disableDelete) {
            const user = userToDelete;
            setUserToDelete(null);
            return await removeProGroupProvider(organizationId, folderId, groupId, user.id);
        }
    }, () => {
        if (disableDelete) {
            setUserToDelete(null);
            setRefresh(r => r + 1);
        }
    }, handleError, () => setDisableDelete(false)), [disableDelete]);
    return addMode ?
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="subtitle1">{t('ProGuest.Groups.Tabs.Providers.Description')}</Typography>
            </Grid>
            <Grid item xs={12}>
                <ProvidersBrowserSimple organizationId={organizationId} folderId={folderId} groupId={groupId}
                                        isFolderProviderSearch={true} action={selectProAction}/>
            </Grid>
            <Grid xs={12} item={true} container justify="flex-end" direction="row" className={classes.actions}>
                {submitted && !processing ?
                    <Button variant="outlined" color="primary" size="medium" onClick={() => {
                        setProcessingState({});
                        setAddMode(false);
                    }}>
                        {t('_Common.Labels.Done')}
                    </Button> :
                    <Fragment>
                        <Button variant="outlined" color="primary" size="medium" disabled={processing}
                                onClick={() => {
                                    setProcessingState({});
                                    setAddMode(false);
                                }}>
                            {t('_Common.Labels.Cancel')}
                        </Button>
                        <Button variant="contained" color="primary" size="medium" disabled={processing}
                                onClick={onSave}>
                            {t('_Common.Labels.Add')}
                        </Button>
                    </Fragment>}
            </Grid>
        </Grid>
        :
        <Grid container spacing={3}>
            <Grid item xs={12} className={classes.actionRow}>
                <Typography variant="subtitle1">{t('ProGuest.Groups.Tabs.Providers.AlreadyIn')}</Typography>
                <Button variant="contained" color="primary" size="medium" onClick={() => {
                    setSubmitted(false);
                    setAddMode(true);
                }}>
                    {t('ProGuest.Groups.AddProvider')}
                </Button>
            </Grid>
            <Grid item xs={12}>
                <GreyedOutProgressMask isActive={disableDelete} displaySpinner={true}>
                    <ProvidersBrowserSimple action={removeProAction} organizationId={organizationId} folderId={folderId}
                                            groupId={groupId} refresh={refresh} isFolderProviderSearch={false}/>
                    <ConfirmationPrompt title={t(`ProGuest.Providers.RemovePro.Title`)}
                                        description={t('ProGuest.Providers.RemovePro.Description')}
                                        open={!!userToDelete} disable={disableDelete}
                                        onCancel={() => setUserToDelete(undefined)} onConfirm={() => deleteProvider()}/>
                </GreyedOutProgressMask>
            </Grid>
            <Grid xs={12} item={true} container justify="flex-end" direction="row" className={classes.actions}>
                <Button variant="outlined" color="primary" size="medium" onClick={onClose}>
                    {t('_Common.Labels.Close')}
                </Button>
            </Grid>
        </Grid>;
};
export default GroupProviders;
